// Modal as a separate component
import React, { useEffect, useRef, PropsWithChildren, useCallback, useContext } from 'react'
import { makeStyles } from '@material-ui/core'
import { IconButton } from 'foundations-library/components'
import { AlignType } from 'foundations-library/types'
import { ScreenSizeContext } from 'providers'
import DialogTitle from './DialogTitle'

const jss = (t) => {
    const { colors } = t['foundations-library']
    const translate = ({ screenSize }) => screenSize.mobile ? 'translateX(-50%)' : 'translateX(-50%) translateY(-50%)'
    return {
        dialog: {
            top: ({ screenSize }) => screenSize.mobile ? '10px' : '50%',
            left: '50%',
            border: 'none',
            borderRadius: ({ screenSize }) => screenSize.mobile ? '20px 20px 0 0' : '20px',
            background: 'white',
            '-webkit-transform': translate,
            '-moz-transform': translate,
            '-ms-transform': translate,
            transform: translate,
            margin: ({ screenSize }) => screenSize.mobile && 'auto 0 0 0',
            width: ({ screenSize }) => screenSize.mobile && '100%',
            maxWidth: ({ screenSize }) => screenSize.mobile && '100%',
            '& .dialog-container': {
                padding: (data) => {
                    const { mobile, tablet } = data.screenSize
                    return mobile || tablet ? '20px' : '40px'
                }
            },
            '&::backdrop': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)'
            },
            '& .close-dialog-x': {
                position: 'absolute',
                top: '20px',
                right: '25px'
            },
            '& .dialog-title': {
                marginBottom: '20px',
                textAlign: ({ titleAlign }) => titleAlign,
                color: colors.neutral[900],
                letterSpacing: '-0.4px'
            }
        }
    }
}

const sizeWidth = {
    small: 400,
    medium: 600,
    large: 900,
    full: '100%',
    '90%': '90%'
}

const useStyles = makeStyles(jss)

interface PropsInterface {
    isOpen: boolean
    onClose?(): any
    onClick?(e): any
    className?: string
    title?: string
    titleAlign?: AlignType
    closeOnClickOutside?: boolean,
    width?: number, // px
    height?: number|string, // px
    size?: 'small' | 'medium' | 'large' | 'full'| '90%',
    onClickBack?(): any,
    style?: Record<string, unknown>,
    'data-testid'?: string
}

/**
 *
 */
const Dialog = ({
    isOpen = false,
    onClose,
    onClick,
    onClickBack,
    children,
    className,
    title,
    titleAlign,
    width,
    height = 'fit-content',
    style = {},
    size = 'medium',
    closeOnClickOutside = true,
    'data-testid': dataTestId = 'dialog'
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    const screenSize = useContext(ScreenSizeContext)
    const styles = useStyles({ screenSize, titleAlign })
    const ref = useRef<HTMLDialogElement | null>(null)

    useEffect(() => {
        if (isOpen) {
            if (ref.current && typeof ref.current.showModal === 'function') {
                ref.current.showModal()
            }
        } else {
            if (ref.current && typeof ref.current.close === 'function') {
                ref.current.close()
            }
        }
    }, [isOpen])

    const elStyle = {
        ...style,
        width: width || sizeWidth[size],
        height: height || 'fit-content'
    }

    const closeDialog = () => onClose && onClose()
    const handleOnClick = useCallback((e) => {
        if (closeOnClickOutside &&
            ref.current &&
            ref.current === e.target
        ) {
            closeDialog()
        }
        if (onClick) onClick(e)
    }, [closeOnClickOutside])
    return (
        <dialog
            ref={ref}
            onCancel={closeDialog}
            className={`${styles.dialog} ${className || ''}`}
            style={elStyle}
            onClick={handleOnClick}
            data-testid={dataTestId}
        >
            <div className="dialog-container" data-testid={`${dataTestId}-container`}>
                {title && <DialogTitle onClickBack={onClickBack} data-testid={`${dataTestId}-title`}>{title}</DialogTitle>}
                <div className="dialog-content" data-testid={`${dataTestId}-content`}>
                    {children}
                </div>
                {
                    onClose &&
                    <IconButton
                        name='close'
                        variant='icon'
                        color='neutral'
                        onClick={closeDialog}
                        className='close-dialog-x'
                        data-testid={`${dataTestId}-close-button`}
                    />
                }
            </div>
        </dialog>
    )
}

/**
 *
 */
export type DialogType = PropsWithChildren<PropsInterface>

export default Dialog
